import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import MoleculeLoader from '../components/MoleculeLoader';
import { BookOpen, Calendar, User, ArrowRight, ChevronLeft, ChevronRight, Grid3X3, LayoutGrid } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ARTICLES_PER_PAGE = 8;

const ResearchInsightsPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blog`);
        if (response.ok) {
          setPosts(await response.json());
        }
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Calculate pagination
  const totalPages = Math.ceil(posts.length / ARTICLES_PER_PAGE);
  const startIndex = (currentPage - 1) * ARTICLES_PER_PAGE;
  const endIndex = startIndex + ARTICLES_PER_PAGE;
  const displayedPosts = showAll ? posts : posts.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleShowAll = () => {
    setShowAll(!showAll);
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title="Research Insights & Articles"
        description="Educational resources and research articles about peptides for laboratory researchers. Learn about purity standards, storage guidelines, and research protocols."
        keywords="peptide research, peptide articles, laboratory research, peptide education, research protocols"
      />
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center">
                <BookOpen className="h-6 w-6 text-teal-700" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold text-slate-900">
                  Research Insights
                </h1>
                <p className="text-slate-600">Educational resources for laboratory researchers</p>
              </div>
            </div>
            {/* Article count and view toggle */}
            {posts.length > ARTICLES_PER_PAGE && (
              <div className="hidden sm:flex items-center gap-3">
                <span className="text-sm text-slate-500">
                  {posts.length} articles
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleShowAll}
                  className={`gap-2 ${showAll ? 'bg-teal-50 border-teal-200 text-teal-700' : ''}`}
                >
                  {showAll ? (
                    <>
                      <Grid3X3 className="h-4 w-4" />
                      Show Pages
                    </>
                  ) : (
                    <>
                      <LayoutGrid className="h-4 w-4" />
                      See All
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {/* Disclaimer */}
        <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-700">
            <strong>Educational Content Only:</strong> These articles are for informational purposes and do not 
            constitute medical advice. All products are intended for laboratory research use only.
          </p>
        </div>

        {/* Mobile view toggle */}
        {posts.length > ARTICLES_PER_PAGE && (
          <div className="sm:hidden mb-6 flex items-center justify-between">
            <span className="text-sm text-slate-500">
              {posts.length} articles
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleShowAll}
              className={`gap-2 ${showAll ? 'bg-teal-50 border-teal-200 text-teal-700' : ''}`}
            >
              {showAll ? 'Show Pages' : 'See All'}
            </Button>
          </div>
        )}

        {loading ? (
          <div className="relative">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl p-4 animate-pulse">
                  <div className="aspect-video bg-slate-200 rounded-xl mb-4"></div>
                  <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-slate-200 rounded w-full mb-2"></div>
                  <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-sm">
                <MoleculeLoader size={72} label="Loading research articles…" />
              </div>
            </div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-slate-900 mb-2">No articles yet</h3>
            <p className="text-slate-500">Check back soon for research insights</p>
          </div>
        ) : (
          <>
            {/* Articles Grid - 4 columns for 8 per page */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {displayedPosts.map((post, index) => (
                <Link 
                  key={post.post_id} 
                  to={`/research-insights/${post.slug}`}
                  className="group"
                >
                  <Card 
                    className="h-full bg-white border-slate-100 overflow-hidden hover:shadow-lg transition-all duration-300 relative"
                    data-testid={`blog-post-${post.slug}`}
                  >
                    {/* Featured Badge for first post on first page */}
                    {index === 0 && currentPage === 1 && !showAll && (
                      <div className="absolute top-3 left-3 z-10">
                        <Badge className="bg-teal-700 text-white text-xs">Featured</Badge>
                      </div>
                    )}
                    
                    <div className="aspect-video overflow-hidden relative">
                      {post.image_url ? (
                        <img 
                          src={(post.image_url?.startsWith('/uploads') || post.image_url?.startsWith('/api/uploads')) ? `${API_URL}${post.image_url}` : post.image_url} 
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-teal-100 to-slate-100 flex items-center justify-center">
                          <BookOpen className="h-8 w-8 text-teal-300" />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(post.published_at).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <h2 className="font-heading font-semibold text-sm sm:text-base text-slate-900 mb-2 group-hover:text-teal-700 transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                      
                      <p className="text-xs text-slate-500 line-clamp-2 mb-3">
                        {post.excerpt}
                      </p>

                      <span className="inline-flex items-center text-xs font-medium text-teal-700 group-hover:gap-1 transition-all">
                        Read More
                        <ArrowRight className="h-3 w-3 ml-1 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {!showAll && totalPages > 1 && (
              <div className="mt-10 flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="gap-1"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="hidden sm:inline">Previous</span>
                </Button>
                
                <div className="flex items-center gap-1">
                  {[...Array(totalPages)].map((_, i) => {
                    const page = i + 1;
                    // Show first, last, current, and adjacent pages
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(page)}
                          className={`w-9 h-9 p-0 ${currentPage === page ? 'bg-teal-700 hover:bg-teal-800' : ''}`}
                        >
                          {page}
                        </Button>
                      );
                    } else if (
                      (page === currentPage - 2 && currentPage > 3) ||
                      (page === currentPage + 2 && currentPage < totalPages - 2)
                    ) {
                      return <span key={page} className="px-1 text-slate-400">...</span>;
                    }
                    return null;
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="gap-1"
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Page info */}
            {!showAll && posts.length > ARTICLES_PER_PAGE && (
              <p className="mt-4 text-center text-sm text-slate-500">
                Showing {startIndex + 1}-{Math.min(endIndex, posts.length)} of {posts.length} articles
              </p>
            )}

            {/* Show All info */}
            {showAll && posts.length > ARTICLES_PER_PAGE && (
              <p className="mt-6 text-center text-sm text-slate-500">
                Showing all {posts.length} articles
              </p>
            )}
          </>
        )}

        {/* Topics Section */}
        <div className="mt-16">
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">Popular Topics</h2>
          <div className="flex flex-wrap gap-3">
            {['Peptide Purity', 'Storage Guidelines', 'COA Interpretation', 'Research Protocols', 'Quality Standards'].map((topic) => (
              <Badge 
                key={topic} 
                variant="outline" 
                className="text-slate-600 border-slate-200 hover:border-teal-200 hover:bg-teal-50 cursor-pointer transition-colors px-4 py-2"
              >
                {topic}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResearchInsightsPage;
